import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { PdfArrangement } from 'src/app/sessions/session/pdf-canvas-item/pdf-services/board-pdf-factory.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BulkUploadService } from '../services/bulk-upload.service';

export interface BulkUploadArrangementOption {
  id: PdfArrangement;
  title: string;
  description: string;
}

@UntilDestroy()
@Component({
  selector: 'app-bulk-upload-arrangement',
  templateUrl: './bulk-upload-arrangement.component.html',
  styleUrls: ['./bulk-upload-arrangement.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BulkUploadArrangementComponent implements OnInit, OnDestroy {
  @Input() selectedArrangement: PdfArrangement = PdfArrangement.Columns;
  @Output() selectionChange = new EventEmitter<PdfArrangement>();

  selectedPagesCount = 0;
  selectedFilesCount = 0;
  readonly options: BulkUploadArrangementOption[] = [
    {
      id: PdfArrangement.Columns,
      title: this.translateService.instant('In columns'),
      description: this.translateService.instant('Files appear next to each other.'),
    },
    {
      id: PdfArrangement.Stacked,
      title: this.translateService.instant('Stacked vertically'),
      description: this.translateService.instant('Files go below one another.'),
    },
  ];

  private readonly subscriptions = new Subscription();

  constructor(
    private readonly translateService: TranslateService,
    private readonly bulkUploadService: BulkUploadService,
  ) {}

  ngOnInit(): void {
    this.subscriptions.add(
      this.bulkUploadService.files$.pipe(untilDestroyed(this)).subscribe((files) => {
        this.selectedFilesCount = files.filter(
          (file) => this.getSelectedPageCount(file) > 0,
        ).length;
        this.selectedPagesCount = files.reduce(
          (sum, file) => sum + this.getSelectedPageCount(file),
          0,
        );
      }),
    );
  }

  selectOption(optionId: PdfArrangement) {
    this.selectionChange.emit(optionId);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private getSelectedPageCount(file: { pages?: number[]; selectedPageIndices?: number[] }): number {
    if (file.selectedPageIndices) {
      return file.selectedPageIndices.length;
    }
    return file.pages?.length ?? 0;
  }
}
